/**
 * PostfaecherPage — Administration › Kommunikation › Postfächer (Story MC6 AC4/5/6/8).
 *
 * The canonical management surface for the broker's OWN connected mailbox(es):
 *   - "Postfach verbinden" opens the shared ProviderChoiceModal (AC2).
 *   - the connection list (mark + Mono email + StatusDot + lastSyncAt + Trennen, AC5).
 *   - honest three-valued availability (available / disabled / absent — AC8): no zero-state
 *     here, but a "in Kürze verfügbar" disabled state and a graceful "nicht verfügbar".
 *
 * The OAuth callback redirects to /kommunikation (a main-app route), because the SWA
 * rewrite serves any /admin/* full-page load the platform-admin bundle. While Kommunikation
 * is switched off, that route forwards `?mailbox=connected|error` here client-side.
 *
 * `requiredKeys: []` (adminNav) → reachable by EVERY authenticated broker via a direct
 * Link, even though the app-shell "Administration" entry stays gated by hasAnyAdminKey.
 *
 * [Source: apps/web/src/features/administration/pages/MandantPage.tsx — page shell + feedback pattern]
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { Button, LoadingState, ErrorState, EmptyState } from '../components';
import {
  useMailboxConnections,
  useDisconnectMailbox,
  useSyncMailboxNow,
  syncResultToToast,
  syncErrorToToast,
} from '../../mailbox/hooks/useMailboxConnections';
import { ProviderChoiceModal } from '../../mailbox/components/ProviderChoiceModal';
import { ConnectionList } from '../../mailbox/components/ConnectionList';
import { DisconnectDialog } from '../../mailbox/components/DisconnectDialog';
import { SyncLevelSettings } from '../../mailbox/components/SyncLevelSettings';
import type { MailboxConnectionDto } from '../../mailbox/types';
import { messageForErrorReason } from '../../mailbox/connect-return';
import { qk } from '../../../lib/query-keys';

const meta = ADMIN_PAGE_META.postfaecher;

type Toast = { kind: 'success' | 'error'; message: string } | null;

export function PostfaecherPage() {
  const { availability, connections, isLoading } = useMailboxConnections();
  const disconnect = useDisconnectMailbox();
  const syncNow = useSyncMailboxNow();
  // A SET, not a single id: two rows can sync at once, and one row's onSettled must not
  // clear another row's spinner early (a single string would).
  const [syncingIds, setSyncingIds] = useState<ReadonlySet<string>>(() => new Set());

  const [modalOpen, setModalOpen] = useState(false);
  const [toDisconnect, setToDisconnect] = useState<MailboxConnectionDto | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  function showToast(kind: 'success' | 'error', message: string) {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 4000);
  }

  // The mailbox-connect OAuth return, forwarded here from /kommunikation while that page
  // is switched off (routeTree.gen.tsx). Mount-only; the param is stripped so a reload
  // does not toast again.
  const qc = useQueryClient();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('mailbox');
    if (result !== 'connected' && result !== 'error') return;
    if (result === 'connected') {
      showToast('success', 'Postfach erfolgreich verbunden.');
      void qc.invalidateQueries({ queryKey: qk.mailboxConnections() });
    } else {
      showToast('error', messageForErrorReason(params.get('reason')));
    }
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  function confirmDisconnect(conn: MailboxConnectionDto) {
    disconnect.mutate(conn.id, {
      onSuccess: () => {
        setToDisconnect(null);
        showToast('success', `Postfach ${conn.email} getrennt.`);
      },
      onError: (e: Error) => showToast('error', e.message || 'Das Postfach konnte nicht getrennt werden.'),
    });
  }

  function triggerSync(conn: MailboxConnectionDto) {
    setSyncingIds((prev) => new Set(prev).add(conn.id));
    syncNow.mutate(conn.id, {
      onSuccess: (r) => {
        const t = syncResultToToast(r);
        showToast(t.kind, t.message);
      },
      onError: (e) => {
        const t = syncErrorToToast(e);
        showToast(t.kind, t.message);
      },
      onSettled: () =>
        setSyncingIds((prev) => {
          const next = new Set(prev);
          next.delete(conn.id);
          return next;
        }),
    });
  }

  const canConnect = availability === 'available';

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={
          <Button disabled={!canConnect} onClick={() => setModalOpen(true)}>
            <Plus className="size-4" /> Postfach verbinden
          </Button>
        }
      />

      {/* Toast (aria-live) */}
      {toast != null && (
        <div
          role={toast.kind === 'error' ? 'alert' : 'status'}
          aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
          className={
            toast.kind === 'error'
              ? 'mb-4 rounded-md border border-status-danger-border bg-status-danger-bg px-3 py-2 text-sm text-status-danger'
              : 'mb-4 rounded-md border border-status-success-border bg-status-success-bg px-3 py-2 text-sm text-status-success'
          }
        >
          {toast.message}
        </div>
      )}

      {/* Body — honest availability + query states */}
      {isLoading ? (
        <LoadingState />
      ) : availability === 'absent' ? (
        <ErrorState message="Die Postfach-Verbindung ist derzeit nicht verfügbar." />
      ) : availability === 'disabled' ? (
        <div className="rounded-xl border border-status-info-border bg-status-info-bg/40 px-4 py-6 text-center text-sm text-status-info">
          Die Postfach-Verbindung wird in Kürze freigeschaltet.
        </div>
      ) : connections.length === 0 ? (
        <EmptyState label="Noch kein Postfach verbunden." />
      ) : (
        <ConnectionList
          connections={connections}
          onDisconnect={setToDisconnect}
          onSyncNow={triggerSync}
          syncingIds={syncingIds}
        />
      )}

      {/* Bereich 1 — e-mail sync-level controls (per-mailbox + tenant cap for admins). */}
      {availability === 'available' && <SyncLevelSettings />}

      <ProviderChoiceModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        availability={availability}
      />
      <DisconnectDialog
        connection={toDisconnect}
        pending={disconnect.isPending}
        onConfirm={confirmDisconnect}
        onClose={() => setToDisconnect(null)}
      />
    </div>
  );
}
